import { assertUnreachable } from "@arti/utilities";
import {
  type CSSProperties,
  type FC,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import {
  getTooltipPosition,
  type TooltipAlign,
  type TooltipPosition,
  type TooltipSide,
} from "./tooltipPosition";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  align?: TooltipAlign;
  className?: string;
  tooltipClassName?: string;
  maxWidthClassName?: string;
  offset?: number;
  side?: TooltipSide;
  triggerTabIndex?: number;
}

function joinClasses(...classNames: (string | undefined)[]): string {
  return classNames.filter(Boolean).join(" ");
}

function getArrowClasses(side: TooltipSide): string {
  switch (side) {
    case "top": {
      return "left-1/2 top-full -translate-x-1/2 -translate-y-1/2 border-r border-b";
    }
    case "bottom": {
      return "bottom-full left-1/2 -translate-x-1/2 translate-y-1/2 border-l border-t";
    }
    case "left": {
      return "left-full top-1/2 -translate-x-1/2 -translate-y-1/2 border-r border-t";
    }
    case "right": {
      return "right-full top-1/2 translate-x-1/2 -translate-y-1/2 border-b border-l";
    }
    default: {
      throw assertUnreachable(side);
    }
  }
}

function getArrowStyle(
  position: TooltipPosition | null,
  fallbackSide: TooltipSide,
): CSSProperties | undefined {
  const side = position?.side ?? fallbackSide;
  switch (side) {
    case "top":
    case "bottom": {
      return position?.arrowLeft == undefined
        ? undefined
        : { left: position.arrowLeft };
    }
    case "left":
    case "right": {
      return position?.arrowTop == undefined
        ? undefined
        : { top: position.arrowTop };
    }
    default: {
      throw assertUnreachable(side);
    }
  }
}

export const Tooltip: FC<TooltipProps> = ({
  children,
  content,
  disabled = false,
  align = "center",
  className,
  tooltipClassName,
  maxWidthClassName = "max-w-xs",
  offset = 10,
  side = "top",
  triggerTabIndex,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || disabled) {
      return;
    }

    function updatePosition() {
      const triggerElement = triggerRef.current;
      const tooltipElement = tooltipRef.current;
      if (!triggerElement || !tooltipElement) {
        return;
      }

      const triggerRect = triggerElement.getBoundingClientRect();
      const tooltipRect = tooltipElement.getBoundingClientRect();
      setPosition(
        getTooltipPosition(triggerRect, tooltipRect, {
          align,
          offset,
          preferredSide: side,
          viewport: { height: window.innerHeight, width: window.innerWidth },
        }),
      );
    }

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, disabled, isOpen, offset, side]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
    }
  }, [isOpen]);

  if (disabled) {
    return <>{children}</>;
  }

  const tooltipStyle: CSSProperties | undefined = position
    ? { left: position.left, top: position.top }
    : undefined;
  const arrowStyle = getArrowStyle(position, side);

  return (
    <>
      <span
        ref={triggerRef}
        aria-describedby={isOpen ? tooltipId : undefined}
        className={joinClasses("inline-flex", className)}
        onBlur={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setIsOpen(false);
          }
        }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        tabIndex={triggerTabIndex}
      >
        {children}
      </span>
      {isOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={joinClasses(
              "pointer-events-none fixed z-[70]",
              maxWidthClassName,
            )}
            style={tooltipStyle}
          >
            <div
              className={joinClasses(
                "relative rounded-none border border-slate-300 bg-white/96 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.14em] leading-relaxed text-slate-700 shadow-[6px_6px_0_rgba(15,23,42,0.12)] backdrop-blur-md dark:border-2 dark:border-slate-700 dark:bg-slate-950/92 dark:text-slate-100 dark:shadow-[8px_8px_0_rgba(2,6,23,0.35)]",
                tooltipClassName,
              )}
            >
              {content}
              <span
                className={joinClasses(
                  "absolute h-2.5 w-2.5 rotate-45 rounded-none border-slate-300 bg-white/96 dark:border-slate-700 dark:bg-slate-950/92",
                  getArrowClasses(position?.side ?? side),
                )}
                style={arrowStyle}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
